import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { toast } from 'react-toastify';
import { ConnectedFacility, ConnectionsService } from '../../../../../js/api';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { connectionRequestsQueryKeys } from '../../../../../js/app/query-keys-factories/connection-requests';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { ConnectionRateInformation } from '../../../components/connection-rate-information';
import { ExpenseAndTermsForm } from '../../../components/expense-and-terms-form';
import { ToggleField } from '../../../components/ToggleField';
import { MENU_BADGES_QUERY_KEY, QUEUE_NOTIFICATIONS_QUERY_KEY } from '../../../constants/queryKeys';
import { StatusCodes, WorkflowScopes } from '../../../constants/workflows';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { formatDateTo24Hr } from '../../../utils/date';
import {
  CONNECTION_CHANGE_HISTORY_QUERY_KEY,
  CONNECTION_QUERY_KEY,
  ORGANIZATION_CONNECTIONS_LIST_QUERY_KEY,
  STAFF_CONNECTIONS_LIST_QUERY_KEY,
} from './queryKeys';

type DetailsTabProps = {
  connection: ConnectedFacility;
};

export function DetailsTab({ connection }: DetailsTabProps) {
  const { audienceIsProvider } = useContext(SessionContext);
  const isProvider = audienceIsProvider ?? false;
  const isPendingConfirm = [StatusCodes.PEND_CONFIRM, StatusCodes.AWAIT_PRVLG].includes(
    connection?.current_status?.code as StatusCodes
  );

  const queryClient = useQueryClient();
  const canUpdateConnection = useHasPermission('update__connections__connectedfacility');
  // Only render when the facility's state actually has a Patient Compensation Fund;
  // the value is only editable while participation there is voluntary.
  const isPcfState = connection.pcf_state_is_mandatory !== null;
  const canEditPcfRequirement =
    canUpdateConnection && !isProvider && connection.pcf_state_is_mandatory === false;
  const pcfMutation = useMutation({
    mutationFn: async (requiresPcfEnrollment: boolean) =>
      ConnectionsService.connectionsConnectedFacilityPartialUpdate({
        id: connection.id,
        requestBody: { require_pcf_enrollment: requiresPcfEnrollment },
      }),
    onSuccess: async () => {
      toast.success('PCF enrollment requirement updated');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [CONNECTION_QUERY_KEY, connection.id] }),
        queryClient.invalidateQueries({ queryKey: [ORGANIZATION_CONNECTIONS_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [STAFF_CONNECTIONS_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists()),
        queryClient.invalidateQueries({
          queryKey: [CONNECTION_CHANGE_HISTORY_QUERY_KEY, connection.id],
        }),
        queryClient.invalidateQueries({
          queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY, WorkflowScopes.ConnectedFacility],
        }),
        queryClient.invalidateQueries({ queryKey: [MENU_BADGES_QUERY_KEY] }),
      ]);
    },
    onError: (error) =>
      handleApiError(error, { messagePrefix: 'Failed to update PCF enrollment requirement' }),
  });

  const createdAt = connection.connected_at
    ? formatDateTo24Hr(connection.connected_at, 'MM/dd/yyyy HH:mm')
    : null;
  const updatedAt = connection.updated_at
    ? formatDateTo24Hr(connection.updated_at, 'MM/dd/yyyy HH:mm')
    : null;

  return (
    <div className="tw:space-y-4">
      <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
        {/* Confirmation Letters */}
        {isPendingConfirm && (
          <div className="tw:col-span-2 tw:flex tw:flex-wrap tw:gap-2">
            <a
              href={`/staff-portal/connections/facility-confirmation-letter/${connection.id}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="tw:inline-block"
            >
              <Button color="brand-green-outline" small>
                <i className="fas fa-file-pdf" />
                Facility Confirmation Ltr
              </Button>
            </a>
            <a
              href={`/staff-portal/connections/clinical-connection-confirmation/${connection.id}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="tw:inline-block"
            >
              <Button color="brand-green-outline" small>
                <i className="fas fa-file-pdf" />
                Clinician Confirmation Ltr
              </Button>
            </a>
          </div>
        )}

        {/* Rate Information */}
        <div className="tw:col-span-2">
          <ConnectionRateInformation connection={connection} />
        </div>

        {/* PCF Enrollment Requirement */}
        {isPcfState && (
          <div className="tw:col-span-2 tw:mt-1" data-testid="connection-pcf-toggle">
            <ToggleField
              title="Requires PCF Enrollment"
              description={
                connection.pcf_state_is_mandatory
                  ? 'Patient Compensation Fund participation is mandatory in this state.'
                  : 'Patient Compensation Fund participation is voluntary in this state.'
              }
              checked={!!connection.require_pcf_enrollment}
              onChange={(checked) => pcfMutation.mutate(checked)}
              disabled={!canEditPcfRequirement || pcfMutation.isPending}
            />
          </div>
        )}

        {/* Created / Last Updated — compact, inline, wraps when tight */}
        <div className="tw:col-span-2 tw:mt-1 tw:flex tw:flex-wrap tw:justify-between tw:gap-x-6 tw:gap-y-1 tw:text-xs tw:text-zinc-500">
          {createdAt && (
            <span>
              <span className="tw:font-medium">Created:</span> {createdAt}
            </span>
          )}
          {updatedAt && updatedAt !== createdAt && (
            <span>
              <span className="tw:font-medium">Last Updated:</span> {updatedAt}
            </span>
          )}
        </div>
      </div>

      {/* Expenses & Terms */}
      <ExpenseAndTermsForm
        entity={connection}
        entityType="connection"
        showTargetHours={!isProvider}
        onUpdate={() => {
          // ExpenseAndTermsForm handles its own query invalidation
        }}
      />
    </div>
  );
}
